import LogsIcon from '@mui/icons-material/History';
// Permissions checked separately
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { Box, Button, Chip, Typography } from '@mui/material';
import { EventRecord, Timeline, TimelineGroup } from '@react-admin/ra-audit-log';
import { useMemo } from 'react';
import { Count, RaRecord, useGetList, useRecordContext, useTranslate } from 'react-admin';

import {
  BOLogIdFieldMap,
  BOLogResource,
  BOLogResponse,
  GetBOLogsQuery,
} from '@Common/types/bo/bo_logs';

import { groupLogsWithUTCLabel } from '@sh4res/bo-common/components/Logs/groupLogsWithUTCLabel';
import { TimelineItem } from '@sh4res/bo-common/components/Logs/TimelineItem';
import { useCanAccess } from '@sh4res/bo-common/hooks';

export type LogsProps = {
  resource: Exclude<BOLogResource, 'settlement'>;
  targetId?: number | ((record: RaRecord) => number);
  filter?: GetBOLogsQuery['filter'];
  /**
   * shows the extra info of every log, whatever its resource and action
   */
  showExtraInfo?: boolean;
};

export function LogsButton(props: LogsProps & { onOpen: () => void; 'data-testid'?: string }) {
  const record = useRecordContext();
  const translate = useTranslate();
  const canAccess = useCanAccess({
    resource: `logs/${props.resource}`,
    action: 'list',
  });
  const idField = BOLogIdFieldMap[props.resource];

  if (!record || !canAccess) {
    return null;
  }

  let targetIdParsed = record.id;
  if (typeof props.targetId === 'function') {
    targetIdParsed = props.targetId(record);
  } else if (typeof props.targetId === 'number') {
    targetIdParsed = props.targetId;
  }

  return (
    <Button
      variant="contained"
      color="info"
      disableElevation={true}
      size="small"
      sx={{
        height: '30px',
        fontWeight: 'bold',
      }}
      startIcon={<LogsIcon />}
      data-testid={props['data-testid']}
      onClick={props.onOpen}>
      <>
        {translate('general.logs.label')}
        <Chip
          size="small"
          sx={{
            height: '20px',
            marginLeft: '5px',
            backgroundColor: '#F8F8F8',
            '& .MuiChip-label': {
              padding: 0,
            },
          }}
          label={
            <Count
              sx={{
                fontWeight: 600,
              }}
              resource={`logs/${props.resource}`}
              filter={{ [idField]: targetIdParsed, ...props.filter }}
            />
          }
        />
      </>
    </Button>
  );
}

export function LogsPanel({ resource, targetId, filter, showExtraInfo }: LogsProps) {
  const record = useRecordContext<RaRecord>();

  let targetIdParsed = record?.id ?? 0;
  if (typeof targetId === 'function') {
    targetIdParsed = targetId(record || { id: 0 });
  } else if (typeof targetId === 'number') {
    targetIdParsed = targetId;
  }

  const idField = BOLogIdFieldMap[resource];

  const {
    data = [],
    isLoading,
    error,
  } = useGetList<BOLogResponse>(
    `logs/${resource}`,
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: 'id', order: 'DESC' },
      filter: { [idField]: targetIdParsed, ...filter },
    },
    { enabled: !!record },
  );

  const raLogs = useMemo(
    () =>
      data.map<EventRecord>((log) => ({
        id: log.id,
        action: log.action,
        author: { id: log.author_id ?? 0, fullName: log.author_email },
        resource: log.resource,
        date: new Date(log.created),
        payload: { data: { name: log.extra_info || ' ' } },
        extra_info: log.extra_info,
      })),
    [data],
  );

  if (error) {
    return (
      <Typography color="error" variant="subtitle1">
        Error Loading Data
      </Typography>
    );
  }

  return (
    <Box sx={{ overflowY: 'auto' }}>
      <Timeline isLoading={isLoading} records={raLogs} groupLogs={groupLogsWithUTCLabel}>
        <TimelineGroup>
          <TimelineItem showExtraInfo={showExtraInfo} />
        </TimelineGroup>
      </Timeline>
    </Box>
  );
}
